import { Controller } from "@hotwired/stimulus"

const SEARCH_DELAY = 250

/**
 * Base controller for picking and uploading images from a gallery.
 * Handles asynchronous loading, searching, pagination, and file uploads.
 */
export default class extends Controller {
  static targets = [
    "imagePicker",
    "imageList",
    "imageUploadInput",
    "imageFirstButton",
    "imagePrevButton",
    "imageNextButton",
    "imageLastButton",
    "imagePaginationStatus",
    "paginationNav",
    "searchInput"
  ]

  static values = {
    loadingMessage: String,
    errorLoadingMessage: String,
    emptyMessage: String,
    uploadErrorMessage: String,
    paginationLabel: String,
    paginationInfoTemplate: String,
    firstPageLabel: String,
    previousPageLabel: String,
    nextPageLabel: String,
    lastPageLabel: String
  }

  connect() {
    this.imagePage = 1
    this.imagePages = 0
    this.imageLoading = false
    this.searchTimeout = null
    this.requestController = null
  }

  disconnect() {
    this.clearSearchTimeout()
    this.abortRequest()
  }

  /**
   * Shows the image picker and loads the initial set of images.
   */
  openImagePicker(event) {
    event.preventDefault()
    this.imagePickerTarget.classList.remove("hidden")
    this.resetAndLoadImages()
    this.focusSearchInput()
  }

  /**
   * Hides the image picker and cancels pending requests.
   */
  closeImagePicker(event) {
    event.preventDefault()
    this.abortRequest()
    this.imagePickerTarget.classList.add("hidden")
  }

  /**
   * Debounces the search input to avoid excessive requests.
   */
  scheduleSearchImages(event) {
    if (event?.type !== "input") return

    this.imagePage = 1
    this.clearSearchTimeout()
    this.searchTimeout = window.setTimeout(() => {
      this.loadImages({ page: 1, reset: true })
    }, SEARCH_DELAY)
  }

  async resetAndLoadImages() {
    this.imagePage = 1
    this.imagePages = 0
    this.clearSearchTimeout()
    await this.loadImages({ page: 1, reset: true })
  }

  async loadPreviousImages(event) {
    event.preventDefault()
    if (this.imageLoading || !this.hasPreviousImages()) return

    this.clearSearchTimeout()
    await this.loadImages({ page: this.imagePage - 1, reset: true })
  }

  async loadFirstImages(event) {
    event.preventDefault()
    if (this.imageLoading || !this.hasFirstImages()) return

    this.clearSearchTimeout()
    await this.loadImages({ page: 1, reset: true })
  }

  async loadNextImages(event) {
    event.preventDefault()
    if (this.imageLoading || !this.hasNextImages()) return

    this.clearSearchTimeout()
    await this.loadImages({ page: this.imagePage + 1, reset: true })
  }

  async loadLastImages(event) {
    event.preventDefault()
    if (this.imageLoading || !this.hasLastImages()) return

    this.clearSearchTimeout()
    await this.loadImages({ page: this.imagePages, reset: true })
  }

  /**
   * Fetches images from the server based on current page and search query.
   */
  async loadImages({ page, reset }) {
    this.abortRequest()
    const requestController = new AbortController()
    this.requestController = requestController
    this.imageLoading = true

    if (reset) {
      this.imageListTarget.replaceChildren()
      this.imageListTarget.appendChild(this.messageElement(this.loadingMessageText(), "text-slate-500"))
    }

    this.updatePaginationUi({ loading: true })

    const params = new URLSearchParams({ page: String(page) })
    const query = this.currentSearchQuery()
    if (query.length > 0) {
      params.set("q[title_cont]", query)
    }

    try {
      const response = await fetch(`/admin/tiptap/gallery_images?${params.toString()}`, {
        headers: { Accept: "application/json" },
        signal: requestController.signal
      })

      if (!response.ok) {
        if (reset) this.imageListTarget.replaceChildren()
        this.imageListTarget.appendChild(this.messageElement(this.errorLoadingMessageText(), "text-red-600"))
        this.showPaginationError(this.errorLoadingMessageText())
        return
      }

      const payload = await response.json()
      const images = payload.items || []
      const pagination = payload.pagination || {}

      this.imageListTarget.replaceChildren()

      if (images.length === 0) {
        this.imageListTarget.appendChild(this.messageElement(this.emptyMessageText(), "text-slate-500"))
      } else {
        images.forEach((image) => this.imageListTarget.appendChild(this.imageButtonElement(image)))
      }

      this.imagePage = pagination.page || page
      this.imagePages = pagination.pages || 0

      this.updatePaginationUi({
        loading: false,
        count: pagination.count || 0,
        from: pagination.from || 0,
        to: pagination.to || 0,
        previous: pagination.previous || null,
        next: pagination.next || null
      })
      this.renderPaginationNav()
    } catch (error) {
      if (error.name !== "AbortError") {
        if (reset) this.imageListTarget.replaceChildren()
        this.imageListTarget.appendChild(this.messageElement(this.errorLoadingMessageText(), "text-red-600"))
        this.showPaginationError(this.errorLoadingMessageText())
      }
    } finally {
      if (this.requestController === requestController) {
        this.imageLoading = false
        this.requestController = null
      }
    }
  }

  /**
   * Handles image upload via POST request and notifies the subclass.
   */
  async uploadImage(event) {
    const file = event.target.files[0]
    if (!file) return

    const formData = new FormData()
    formData.append("gallery_image[image]", file)
    formData.append("gallery_image[title]", file.name)

    const response = await fetch("/admin/tiptap/gallery_images", {
      method: "POST",
      headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken() },
      body: formData
    })

    if (!response.ok) {
      window.alert(this.uploadErrorMessageText())
      return
    }

    const image = await response.json()
    this.afterUploadImage(image)
    this.imageUploadInputTarget.value = ""
  }

  /**
   * Creates a button element for a gallery image.
   */
  imageButtonElement(image) {
    const button = document.createElement("button")
    const selected = String(this.selectedImageValue() || "") === String(image.record_id || image.id || "")

    button.type = "button"
    button.className = [
      "group cursor-pointer overflow-hidden rounded-lg border bg-white text-left transition",
      selected ? "border-sky-500 ring-2 ring-sky-200" : "border-slate-200 hover:border-slate-400"
    ].join(" ")

    const img = document.createElement("img")
    img.src = image.thumb_url || image.url
    img.alt = image.alt || ""
    img.className = "aspect-video w-full object-cover"
    img.loading = "lazy"

    const title = document.createElement("span")
    title.className = "block truncate px-2 py-1 text-xs text-slate-600"
    title.textContent = image.title || ""

    button.appendChild(img)
    button.appendChild(title)
    button.addEventListener("click", () => this.onImageSelect(image))
    return button
  }

  messageElement(message, colorClass) {
    const element = document.createElement("p")
    element.className = `col-span-full text-sm ${colorClass}`
    element.textContent = message
    return element
  }

  /**
   * Updates the pagination status text and button states.
   */
  updatePaginationUi({ loading = false, count = 0, from = 0, to = 0, previous = null, next = null } = {}) {
    if (this.hasImagePaginationStatusTarget) {
      if (loading) {
        this.imagePaginationStatusTarget.textContent = this.loadingMessageText()
      } else if (count > 0) {
        this.imagePaginationStatusTarget.textContent = this.paginationInfoText({ from, to, count })
      } else {
        this.imagePaginationStatusTarget.textContent = this.emptyMessageText()
      }
    }

    this.updateNavigationButtons({ loading, previous, next })
  }

  /**
   * Renders the complex pagination navigation links.
   */
  renderPaginationNav() {
    if (!this.hasPaginationNavTarget) return

    if (!this.imagePages || this.imagePage < 1) {
      this.paginationNavTarget.replaceChildren()
      this.paginationNavTarget.classList.toggle("hidden", true)
      return
    }

    const nav = document.createElement("nav")
    nav.className = "pagy series-nav"
    nav.setAttribute("aria-label", this.paginationLabelText())

    nav.append(
      this.navigationLink({
        label: this.firstPageLabelText(),
        iconClass: "ti ti-chevrons-left",
        page: 1,
        disabled: !this.hasFirstImages(),
        current: false,
        itemType: "first"
      }),
      this.navigationLink({
        label: this.previousPageLabelText(),
        iconClass: "ti ti-chevron-left",
        page: this.imagePage - 1,
        disabled: !this.hasPreviousImages(),
        current: false,
        itemType: "previous"
      })
    )

    this.pageSeries().forEach((item) => {
      if (item === null) {
        nav.append(this.separatorLink())
        return
      }

      nav.append(
        this.navigationLink({
          label: String(item),
          page: item,
          disabled: false,
          current: item === this.imagePage,
          itemType: "page"
        })
      )
    })

    nav.append(
      this.navigationLink({
        label: this.nextPageLabelText(),
        iconClass: "ti ti-chevron-right",
        page: this.imagePage + 1,
        disabled: !this.hasNextImages(),
        current: false,
        itemType: "next"
      }),
      this.navigationLink({
        label: this.lastPageLabelText(),
        iconClass: "ti ti-chevrons-right",
        page: this.imagePages,
        disabled: !this.hasLastImages(),
        current: false,
        itemType: "last"
      })
    )

    this.paginationNavTarget.replaceChildren(nav)
    this.paginationNavTarget.classList.toggle("hidden", false)
    this.updateNavigationButtons({ loading: false })
  }

  /**
   * Creates a single navigation link for pagination.
   */
  navigationLink({ label, iconClass = null, page, disabled, current, itemType }) {
    const link = document.createElement("a")
    link.setAttribute("aria-label", label)

    if (current) {
      link.setAttribute("aria-current", "page")
      link.textContent = label
    } else if (disabled) {
      link.setAttribute("role", "link")
      link.setAttribute("aria-disabled", "true")
      link.textContent = iconClass ? "" : label
      if (iconClass) {
        link.append(this.iconElement(iconClass))
      }
    } else {
      link.href = "#"
      link.dataset.page = String(page)
      link.dataset.itemType = itemType
      link.addEventListener("click", (event) => this.navigateToPage(event))
      if (iconClass) {
        link.append(this.iconElement(iconClass))
      } else {
        link.textContent = label
      }
    }

    return link
  }

  separatorLink() {
    const link = document.createElement("a")
    link.setAttribute("role", "separator")
    link.setAttribute("aria-disabled", "true")
    link.textContent = "…"
    return link
  }

  navigateToPage(event) {
    event.preventDefault()
    const page = Number(event.currentTarget.dataset.page)
    if (!Number.isInteger(page) || page < 1 || this.imageLoading) return

    this.clearSearchTimeout()
    this.loadImages({ page, reset: true })
  }

  iconElement(className) {
    const icon = document.createElement("i")
    icon.className = className
    icon.setAttribute("aria-hidden", "true")
    return icon
  }

  paginationInfoText({ from, to, count }) {
    return this.paginationInfoTemplateText()
      .replaceAll("__FROM__", String(from))
      .replaceAll("__TO__", String(to))
      .replaceAll("__COUNT__", String(count))
  }

  paginationLabelText() {
    return this.paginationLabelValue || "Pagination"
  }

  firstPageLabelText() {
    return this.firstPageLabelValue || "First page"
  }

  previousPageLabelText() {
    return this.previousPageLabelValue || "Previous page"
  }

  nextPageLabelText() {
    return this.nextPageLabelValue || "Next page"
  }

  lastPageLabelText() {
    return this.lastPageLabelValue || "Last page"
  }

  paginationInfoTemplateText() {
    return this.paginationInfoTemplateValue || "Showing __FROM__ to __TO__ of __COUNT__ items"
  }

  currentSearchQuery() {
    return this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ""
  }

  /**
   * Sets focus to the search input and moves cursor to the end.
   */
  focusSearchInput() {
    if (!this.hasSearchInputTarget) return

    requestAnimationFrame(() => {
      this.searchInputTarget.focus()
      if (typeof this.searchInputTarget.setSelectionRange === "function") {
        const length = this.searchInputTarget.value.length
        this.searchInputTarget.setSelectionRange(length, length)
      }
    })
  }

  /**
   * Calculates the series of pages to show in the pagination bar.
   */
  pageSeries() {
    if (this.imagePages <= 1) {
      return [1]
    }

    if (this.imagePages <= 7) {
      return Array.from({ length: this.imagePages }, (_, index) => index + 1)
    }

    const pages = new Set([1, this.imagePages, this.imagePage - 1, this.imagePage, this.imagePage + 1])
    return Array.from(pages)
      .filter((page) => page >= 1 && page <= this.imagePages)
      .sort((left, right) => left - right)
      .flatMap((page, index, array) => {
        if (index === 0) return [page]
        if (page - array[index - 1] > 1) return [null, page]
        return [page]
      })
  }

  /**
   * Updates disabled state of the static navigation buttons.
   */
  updateNavigationButtons({ loading = false, previous = null, next = null } = {}) {
    if (this.hasImageFirstButtonTarget) {
      this.imageFirstButtonTarget.disabled = loading || !this.hasFirstImages()
    }

    if (this.hasImagePrevButtonTarget) {
      const canGoPrev = previous === null ? this.hasPreviousImages() : !!previous
      this.imagePrevButtonTarget.disabled = loading || !canGoPrev
    }

    if (this.hasImageNextButtonTarget) {
      const canGoNext = next === null ? this.hasNextImages() : !!next
      this.imageNextButtonTarget.disabled = loading || !canGoNext
    }

    if (this.hasImageLastButtonTarget) {
      this.imageLastButtonTarget.disabled = loading || !this.hasLastImages()
    }
  }

  showPaginationError(message) {
    if (this.hasImagePaginationStatusTarget) {
      this.imagePaginationStatusTarget.textContent = message
    }

    this.updateNavigationButtons({ loading: false, previous: true, next: true })
  }

  loadingMessageText() {
    return this.loadingMessageValue || "Caricamento immagini..."
  }

  errorLoadingMessageText() {
    return this.errorLoadingMessageValue || "Errore durante il caricamento delle immagini."
  }

  emptyMessageText() {
    return this.emptyMessageValue || "Nessuna immagine disponibile."
  }

  uploadErrorMessageText() {
    return this.uploadErrorMessageValue || "Errore durante il caricamento dell'immagine."
  }

  selectedImageValue() {
    return null
  }

  abortRequest() {
    if (this.requestController) {
      this.requestController.abort()
      this.requestController = null
    }
  }

  clearSearchTimeout() {
    if (this.searchTimeout) {
      window.clearTimeout(this.searchTimeout)
      this.searchTimeout = null
    }
  }

  hasFirstImages() {
    return this.imagePage > 1
  }

  hasPreviousImages() {
    return this.imagePage > 1
  }

  hasNextImages() {
    return this.imagePages > 0 && this.imagePage < this.imagePages
  }

  hasLastImages() {
    return this.imagePages > 0 && this.imagePage < this.imagePages
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }

  // Hooks for subclasses
  afterUploadImage(_image) {}

  onImageSelect(_image) {}
}
